import React from "react";
import CollapsibleSection from "./CollapsibleSection";

const DealerSitePanel = ({
    isOpen,
    onToggle,
    subdomain,
    setSubdomain,
    subdomainRef,
    subdomainInputRef,
    availability,
    checkMessage,
    checking,
    validationErrors,
    setValidationErrors,
    dealerURL,
    formData,
    setFormData,
    themes,
    themeRef,
    scrollToRef,
    setScrollToRef,
    handleCheckboxChange,
    status,
    originalSubdomain,
}) => {
    const isSubdomainLocked = ["Approved", "Live"].includes(status) && !!originalSubdomain;

    return (
        <CollapsibleSection
            title="🔧 Build Your Dealer Site"
            isOpen={isOpen}
            onToggle={onToggle}
            afterExpand={() => {
                setTimeout(() => {
                    if (scrollToRef?.current) {
                        const el = scrollToRef.current;
                        const y = el.getBoundingClientRect().top + window.scrollY - 100;
                        window.scrollTo({ top: y, behavior: "smooth" });
                        el.focus?.();
                        setScrollToRef(null);
                    }
                }, 0);
            }}
        >
            <div className="form-section" ref={subdomainRef}>
                <div className="space-div"></div>
                <label><span className="required">**</span> Choose Your Custom Web Address</label>
                <small className="note overviewNoteSite">
                    Choose your custom web address and select a site theme that controls the layout, colors, and branding of your dealer website
                </small>

                <div className="subdomain-check-wrapper">
                    <input
                        id="web_subdomain"
                        name="web_subdomain"
                        type="text"
                        placeholder="e.g. gun_world"
                        value={subdomain}
                        ref={subdomainInputRef}
                        readOnly={isSubdomainLocked} // 🔒 lock when status is Approved or Live
                        className={`your-input-class ${isSubdomainLocked ? "readonly" : ""} ${validationErrors.subdomain || availability === false || checkMessage ? "input-error" : ""}`}
                        onChange={(e) => {
                            if (isSubdomainLocked) return; // 🔒 extra guard
                            const val = e.target.value;
                            setSubdomain(val);
                            if (val.trim() && availability === true && !checkMessage) {
                                setValidationErrors(prev => ({ ...prev, subdomain: false }));
                            }
                        }}
                    />

                    {checking && <span className="checking-msg">🔄 Checking...</span>}

                    {!isSubdomainLocked && !checking && availability === true && subdomain && (
                        <span className="available-msg">✅ Web site address is available</span>
                    )}

                    {!checking && availability === false && subdomain && (
                        <span className="taken-msg">❌ {checkMessage || "Sorry, already taken. Try another one."}</span>
                    )}

                    {isSubdomainLocked && (
                        <small className="locked-subdomain-msg">
                            ** Subdomain is locked after approval. Contact support to request a change.
                        </small>
                    )}
                </div>

                {!isSubdomainLocked && (
                    <>
                        <small className="note">
                            Your custom web address is free. Website hosting is a separate service; review the published hosting offer for the trial duration, price, and billing terms before publishing.&nbsp;
                            <strong className="text-yellow">
                                {dealerURL && availability === true ? (
                                    <a href={dealerURL} target="_blank" rel="noopener noreferrer">
                                        {dealerURL.replace(/^https?:\/\//, "")}
                                    </a>
                                ) : (
                                    "yourname.ffls.io"
                                )}
                            </strong>
                        </small>

                        <div className="space-div-narrow"></div>
                        <small className="note note-subdomain">
                            ** Must be 3–20 characters. Letters, numbers, and underscores only.
                        </small>
                    </>
                )}

                <div className="space-div"></div>
                <div className="space-div-line"></div>

                {/* --- THEME --- */}
                <label><span className="required">**</span> Choose Your Theme</label>
                <small className="note themeNote">
                    Controls the visual style of your dealer website — colors, layout, and branding.
                </small>

                <div className="space-div" ref={themeRef}></div>

                {validationErrors.theme && (
                    <small className="field-error field-error-theme">
                        ** Please select a theme for your dealer website.
                    </small>
                )}

                <div className="mobile-theme-view">
                    <select
                        value={formData.theme}
                        onChange={(e) => {
                            setFormData({ ...formData, theme: e.target.value });
                            setValidationErrors(prev => ({ ...prev, theme: false }));
                        }}
                    >
                        {themes.map((theme) => (
                            <option key={theme.template_name} value={theme.template_name}>
                                {theme.display_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="desktop-theme-view">
                    <div className="space-div"></div>
                    <div className="theme-grid">
                        {themes.map((theme) => (
                            <div
                                key={theme.template_name}
                                className={`theme-card ${formData.theme === theme.template_name ? "selected" : ""}`}
                                onClick={() => {
                                    setFormData((prev) => ({ ...prev, theme: theme.template_name }));
                                    setValidationErrors(prev => ({ ...prev, theme: false }));
                                }}
                            >
                                <img
                                    loading="lazy"
                                    src={`/assets/templates/thumbnails/${theme.thumbnail}?v=approved-20261018-662`}
                                    alt={theme.display_name}
                                />
                                <div className="theme-name">{theme.display_name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-div"></div>
                <div className="space-div-line"></div>

                <div className="age-gate-check">
                    <label>
                        <input
                            type="checkbox"
                            name="ageGate"
                            id="ageGate"
                            checked={formData.ageGate}
                            onChange={(e) => {
                                handleCheckboxChange(e);
                            }}
                        />
                        <label htmlFor="ageGate">&nbsp;&nbsp;Require age confirmation (18+ only)</label>
                        <small className="note age-gate-description">
                            Requiring users to confirm they are 18 or older before accessing your website.
                        </small>
                    </label>
                </div>
                <div className="space-div"></div>

            </div>
        </CollapsibleSection>
    );
};

export default DealerSitePanel;
